import { InteractionType } from '@azure/msal-browser';
import { useMsal, useMsalAuthentication, useAccount } from '@azure/msal-react';
import { default as React, useEffect, type FC } from 'react';
import { pageview } from 'react-ga';
import { Route, Switch, useLocation } from 'react-router';

import { Content } from '@dazn/dx-components';
import { RmsFeedback, RmsButton } from '@dazn/dx-rate-my-service-components-react';

import { Header } from './components/Header';
import authConfig from './config/auth';
import { APP_NAME, RMS_ENV, RMS_SERVICE_NAME, ALLOWED_USERS } from './config/constants';
import { routes } from './pages/routes';

const request = {
  scopes: [`${authConfig.clientId}/.default`],
  authority: authConfig.authority,
};

const App: FC = () => {
  const location = useLocation();
  useMsalAuthentication(InteractionType.Redirect);
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);

  useEffect(() => {
    // After the AAD redirect flow, set the active account so that
    // acquireTokenSilent calls (e.g. in useRequest) always have a valid account.
    if (!instance.getActiveAccount() && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
    }
  }, [instance, accounts]);

  useEffect(() => {
    pageview(location.pathname);
  }, [location]);

  const fetchToken = async () => instance.acquireTokenSilent(request).then(({ accessToken: token }) => token);

  if (
    typeof account !== 'undefined' &&
    account !== null &&
    ALLOWED_USERS.length > 0 &&
    !ALLOWED_USERS.includes(account.username.toLowerCase())
  ) {
    return (
      <div style={{ margin: '20%', textAlign: 'center' }}>
        You are logged in as: {account.username}, but only explicitly allowed users can access this project. If you need
        to access this application, please reach out to the <a href="https://dazn.link/support/dx">DX team</a>.
      </div>
    );
  }

  return (
    <>
      <Header />
      <Content>
        <Switch>
          {Object.values(routes).map((route, idx) => (
            <Route key={idx} path={route.path} component={route.component} exact={true} />
          ))}
        </Switch>
        <RmsFeedback
          displayName={APP_NAME}
          serviceName={RMS_SERVICE_NAME}
          endpointEnv={RMS_ENV}
          author={account?.name ?? ''}
          fetchToken={fetchToken}
        >
          <RmsButton />
        </RmsFeedback>
      </Content>
      <footer className="container mt-5 text-center">
        <p>Please remember that this tool and links are only for internal use</p>
        <ul className="list-inline">
          <li className="list-inline-item">
            <a target="_blank" rel="noopener noreferrer" title="Get in touch 🗣" href="https://dazn.link/support/dx">
              Get in touch
              <span className="text-decoration-none d-inline-block ml-1">🗣</span>
            </a>
          </li>
          <li className="list-inline-item">
            <RmsFeedback
              displayName={APP_NAME}
              serviceName={RMS_SERVICE_NAME}
              endpointEnv={RMS_ENV}
              author={account?.name ?? ''}
              fetchToken={fetchToken}
            >
              <a href="/#">
                Report an issue
                <span className="text-decoration-none d-inline-block ml-1">🐛</span>
              </a>
            </RmsFeedback>
          </li>
          <li className="list-inline-item">
            <a href={routes.logout.path} data-test-id="logout-button">
              Logout
              <span className="text-decoration-none d-inline-block ml-1">🚪</span>
            </a>
          </li>
        </ul>
        Made with <span className="text-decoration-none d-inline-block">❤️</span> by{' '}
        <a
          target="_blank"
          rel="noopener noreferrer"
          title="PLATFORM 🤘"
          href="https://livesport.atlassian.net/wiki/spaces/DP/overview"
        >
          <b>PLAT</b>FORM
          <span className="text-decoration-none d-inline-block ml-1">🤘</span>
        </a>
      </footer>
    </>
  );
};

export default App;
